
import React from 'react';

const TreatmentOptions = () => {
  const treatments = [
    {
      name: 'Growth Hormone Peptides',
      description: 'Stimulate natural growth hormone production for enhanced recovery and anti-aging benefits',
      peptides: ['Sermorelin', 'Ipamorelin', 'CJC-1295', 'GHRP-6'],
      benefits: ['Improved sleep quality', 'Enhanced muscle growth', 'Faster recovery', 'Anti-aging effects'],
      image: 'https://public.readdy.ai/ai/img_res/5078c9bffd95d656b927c4b916358750.jpg'
    },
    {
      name: 'Healing & Recovery Peptides',
      description: 'Accelerate tissue repair and reduce inflammation for optimal healing',
      peptides: ['BPC-157', 'TB-500', 'Thymosin Beta-4', 'GHK-Cu'],
      benefits: ['Faster wound healing', 'Reduced inflammation', 'Joint repair', 'Tissue regeneration'],
      image: 'https://readdy.ai/api/search-image?query=Healing%20peptides%20for%20tissue%20repair%2C%20medical%20laboratory%20with%20recovery%20therapy%20equipment%2C%20clean%20white%20background%20with%20green%20accents%2C%20professional%20wellness%20clinic%20setting&width=600&height=400&seq=healing-peptides&orientation=landscape'
    },
    {
      name: 'Cognitive Enhancement Peptides',
      description: 'Optimize brain function, memory, and mental clarity with nootropic peptides',
      peptides: ['Noopept', 'Cerebrolysin', 'Dihexa', 'NSI-189'],
      benefits: ['Enhanced memory', 'Improved focus', 'Mental clarity', 'Neuroprotection'],
      image: 'https://readdy.ai/api/search-image?query=Cognitive%20enhancement%20peptides%20for%20brain%20health%2C%20modern%20neuroscience%20laboratory%20with%20brain%20imaging%20technology%2C%20clean%20medical%20setting%20with%20purple%20and%20blue%20accents&width=600&height=400&seq=cognitive-peptides&orientation=landscape'
    },
    {
      name: 'Metabolic Optimization Peptides',
      description: 'Enhance metabolism, weight management, and energy production',
      peptides: ['Semaglutide', 'AOD-9604', 'Tesamorelin', 'MOTS-c'],
      benefits: ['Weight loss support', 'Improved metabolism', 'Enhanced energy', 'Blood sugar control'],
      image: 'https://readdy.ai/api/search-image?query=Metabolic%20peptides%20for%20weight%20management%2C%20modern%20wellness%20clinic%20with%20metabolism%20testing%20equipment%2C%20clean%20white%20background%20with%20orange%20accents%2C%20professional%20healthcare%20setting&width=600&height=400&seq=metabolic-peptides&orientation=landscape'
    },
    {
      name: 'Immune System Peptides',
      description: 'Strengthen immune function and enhance overall health resilience',
      peptides: ['Thymosin Alpha-1', 'LL-37', 'Epithalon', 'Selank'],
      benefits: ['Immune support', 'Stress reduction', 'Better sleep', 'Longevity benefits'],
      image: 'https://readdy.ai/api/search-image?query=Immune%20system%20peptides%20in%20medical%20laboratory%2C%20clean%20healthcare%20setting%20with%20immune%20cell%20imagery%2C%20white%20background%20with%20teal%20accents%2C%20professional%20peptide%20therapy%20equipment&width=600&height=400&seq=immune-peptides&orientation=landscape'
    },
    {
      name: 'Performance Enhancement Peptides',
      description: 'Optimize athletic performance, endurance, and physical capabilities',
      peptides: ['Follistatin', 'Hexarelin', 'IGF-1 LR3', 'Mechano Growth Factor'],
      benefits: ['Increased strength', 'Enhanced endurance', 'Muscle growth', 'Performance gains'],
      image: 'https://readdy.ai/api/search-image?query=Performance%20enhancement%20peptides%20for%20athletes%2C%20modern%20sports%20medicine%20clinic%20with%20fitness%20equipment%2C%20clean%20white%20background%20with%20red%20accents%2C%20professional%20athletic%20therapy%20setting&width=600&height=400&seq=performance-peptides&orientation=landscape'
    }
  ];

  return (
    <section className="py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
            Comprehensive <strong>Peptide Treatment</strong> Options
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Our extensive peptide library offers targeted solutions for every health and performance goal, backed by cutting-edge research and clinical expertise.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {treatments.map((treatment, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <div className="aspect-video overflow-hidden">
                <img 
                  src={treatment.image} 
                  alt={treatment.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              
              <div className="p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">
                  {treatment.name}
                </h3>
                <p className="text-gray-600 mb-4 lg:mb-6 leading-relaxed">
                  {treatment.description}
                </p>

                <div className="mb-4 lg:mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Key Peptides:</h4>
                  <div className="flex flex-wrap gap-2">
                    {treatment.peptides.map((peptide, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                        {peptide}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Benefits:</h4>
                  <ul className="space-y-2">
                    {treatment.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center">
                        <i className="ri-check-line text-blue-600 text-lg mr-3"></i>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 lg:mt-16">
          <div className="bg-blue-50 rounded-2xl p-6 lg:p-8 max-w-4xl mx-auto">
            <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">
              Personalized Peptide Protocols
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Our expert team creates customized peptide combinations tailored to your specific health goals, lifestyle, and biomarkers for optimal results.
            </p>
            <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Get Your Custom Protocol
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TreatmentOptions;
